import { Routes, Route, Navigate } from "react-router-dom"
import { LoginPage } from "@/pages/login"
import { SignupPage } from "@/pages/signup"
import { ForgotPasswordPage } from "@/pages/forgot-password"
import { ResetPasswordPage } from "@/pages/reset-password"
import ConfirmationPage from "@/pages/confirmation"
import PasswordSetupPage from "@/pages/password-setup"
import PaymentSetupPage from "@/pages/payment-setup"
import OrganizationSetupPage from "@/pages/organization-setup"
import { DashboardLayout } from "@/layouts/dashboard-layout"
// import { DashboardContent } from "@/pages/dashboard-content"
import { EdgePage } from "@/pages/edge"
import { OptimizerPage } from "@/pages/optimizer"
import { OrganizationsPage } from "@/pages/organizations"
import { IntegrationsPage } from "@/pages/integrations"
import { EditIntegrationPage } from "@/pages/integrations/edit"
import { NormalizationsPage } from "@/pages/normalizations"
import { ModelPage } from "@/pages/model"
import { ReportsPage } from "@/pages/reports"
import { AnalysisPage } from "@/pages/analysis"
import { MonitoringPage } from "@/pages/monitoring"
import { HealthPage } from "@/pages/health"
import { NotificationsPage } from "@/pages/notifications"
import { AgentDetailPage } from "@/pages/agent-detail"
import { ExecutionDetailPage } from "@/pages/execution-detail"
import SettingsPage from "@/pages/settings"
import HelpPage from "@/pages/help"
import AccountPage from "@/pages/account"
import { OAuthIntegrationPage } from "@/pages/oauth-integration"
import { OAuthCallbackPage } from "@/pages/oauth-callback"
import { GuardianPage } from "@/pages/guardian"
import { AppsSdkPage } from "@/pages/admin-apps-sdk"
import { ProtectedRoute } from "@/components/protected-route"
import { SubscriptionGuard } from "@/components/subscription-guard"
import UpgradeRequiredPage from "@/pages/upgrade-required"
import { OrganizationProvider } from "@/contexts/organization-context"
import { OrgRouteValidator } from "@/components/org-route-validator"
import { useAuth } from "@/contexts/auth-context"
import { usePageTracking } from "@/hooks/useTracking"
import { useVersionCheck } from "@/hooks/useVersionCheck"

// Component to handle root redirect based on auth status
function RootRedirect() {
  const { user, isSessionValid, validationInProgress, loading } = useAuth()
  const lastOrg = localStorage.getItem('activeOrganization')

  // Show loading while initializing or validating session
  if (loading || validationInProgress) {
    return <div className="flex items-center justify-center min-h-screen">
      <div className="animate-pulse">Loading...</div>
    </div>
  }

  // If authenticated and user exists
  if (isSessionValid && user) {
    // Check if user is in setup flow - don't redirect them away from it
    const currentPath = window.location.pathname
    if (currentPath.startsWith('/get-started/')) {
      // User is in setup flow, let them continue
      return null
    }

    // If we have a last organization, go to optimizer page
    if (lastOrg) {
      return <Navigate to={`/${lastOrg}/optimizer`} replace />
    }

    // Otherwise go to organizations page
    return <Navigate to="/organizations" replace />
  }

  // Not authenticated, go to login
  return <Navigate to="/login" replace />
}

function App() {
  // Track page views automatically
  usePageTracking()

  // Check for new versions and auto-reload
  useVersionCheck()

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset_password/:token" element={<ResetPasswordPage />} />
      <Route path="/get-started/:token" element={<ConfirmationPage />} />
      <Route path="/get-started/profile" element={<PasswordSetupPage />} />
      <Route path="/get-started/payment" element={<PaymentSetupPage />} />
      <Route path="/get-started/organization" element={<OrganizationSetupPage />} />
      <Route path="/organizations/new" element={<OrganizationSetupPage />} />
      <Route path="/organizations" element={
        <ProtectedRoute>
          <OrganizationProvider>
            <DashboardLayout />
          </OrganizationProvider>
        </ProtectedRoute>
      }>
        <Route index element={<OrganizationsPage />} />
      </Route>
      {/* OAuth integration route without authentication for popup windows */}
      <Route path="/:orgSlug/oauth_integration" element={
        <OrganizationProvider>
          <OAuthIntegrationPage />
        </OrganizationProvider>
      } />
      {/* Billing block lands here. Outside SubscriptionGuard: a guarded route would loop. */}
      <Route path="/:orgSlug/upgrade" element={
        <ProtectedRoute>
          <OrganizationProvider>
            <OrgRouteValidator>
              <UpgradeRequiredPage />
            </OrgRouteValidator>
          </OrganizationProvider>
        </ProtectedRoute>
      } />
      <Route path="/:orgSlug" element={
        <ProtectedRoute>
          <OrganizationProvider>
            <OrgRouteValidator>
              <SubscriptionGuard>
                <DashboardLayout />
              </SubscriptionGuard>
            </OrgRouteValidator>
          </OrganizationProvider>
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="optimizer" replace />} />
        <Route path="optimizer" element={<OptimizerPage />} />
        <Route path="scenarios" element={<EdgePage />} />
        <Route path="integrations" element={<IntegrationsPage />} />
        <Route path="integrations/:integrationId/edit" element={<EditIntegrationPage />} />
        <Route path="model" element={<ModelPage />} />
        <Route path="normalizations" element={<NormalizationsPage />} />
        <Route path="monitoring" element={<MonitoringPage />} />
        <Route path="health" element={<HealthPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="analysis" element={<AnalysisPage />} />
        <Route path="analysis/:id" element={<AnalysisPage />} />
        <Route path="agents/:agentId" element={<AgentDetailPage />} />
        <Route path="agents/:agentId/executions/:executionId" element={<ExecutionDetailPage />} />
        <Route path="guardian" element={<GuardianPage />} />
        <Route path="admin/apps-sdk" element={<AppsSdkPage />} />
        {/* Deep link to a specific SDK + wizard tab: target = `<buildId>_<step>` */}
        <Route path="admin/apps-sdk/:target" element={<AppsSdkPage />} />
        <Route path="agents" element={<NotificationsPage />} />
        {/* Legacy URL: keep old /recommendations links working */}
        <Route path="recommendations" element={<Navigate to="../agents" replace />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="help" element={<HelpPage />} />
        <Route path="account" element={<AccountPage />} />
      </Route>
      {/* OAuth callback route at root level for provider redirects - no auth required */}
      <Route path="/oauth/callback" element={<OAuthCallbackPage />} />
      <Route path="/" element={<RootRedirect />} />
      {/* Catch all unknown routes and redirect based on auth */}
      <Route path="*" element={<RootRedirect />} />
    </Routes>
  )
}

export default App